import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import RxntService from '../services/RxntService';
import { OK } from '../utils/constant';

export const fetchRxntSettings = createAsyncThunk(
  'rxnt/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await RxntService.getSettings();
      if (response.code === OK) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRxntPrescriptions = createAsyncThunk(
  'rxnt/fetchPrescriptions',
  async (visitId, { rejectWithValue }) => {
    try {
      const response = await RxntService.getPrescriptions(visitId);
      if (response.code === OK) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRxntProvidersByClinic = createAsyncThunk(
  'rxnt/fetchProvidersByClinic',
  async (clinicId, { rejectWithValue }) => {
    try {
      const response = await RxntService.getProvidersByClinic(clinicId);
      if (response.code === OK) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchPharmacies = createAsyncThunk(
  'rxnt/searchPharmacies',
  async (arg, { rejectWithValue }) => {
    try {
      const payload =
        typeof arg === 'string' ? { search: arg } : { ...arg };
      const response = await RxntService.searchPharmacies(payload);
      if (response.code === OK) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchMedications = createAsyncThunk(
  'rxnt/searchMedications',
  async (arg, { rejectWithValue }) => {
    try {
      const payload =
        typeof arg === 'string' ? { search: arg } : { ...arg };
      const response = await RxntService.searchMedications(payload);
      if (response.code === OK) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createPrescription = createAsyncThunk(
  'rxnt/createPrescription',
  async (data, { rejectWithValue }) => {
    try {
      const response = await RxntService.createPrescription(data);
      if (!response || typeof response !== 'object') {
        return rejectWithValue('RXNT request failed. Please check patient demographics and try again.');
      }
      if (response.code === 201 || response.code === OK) {
        return response.data;
      }
      return rejectWithValue(response.message || 'RXNT request failed.');
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'RXNT request failed.';
      return rejectWithValue(message);
    }
  }
);

export const voidPrescription = createAsyncThunk(
  'rxnt/voidPrescription',
  async ({ prescriptionId, comments, voidCode, reason: legacyReason }, { rejectWithValue }) => {
    try {
      const response = await RxntService.voidPrescription(prescriptionId, {
        comments: comments ?? legacyReason,
        voidCode: voidCode ?? 'NONE',
      });
      if (response.code === OK) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const refillPrescription = createAsyncThunk(
  'rxnt/refillPrescription',
  async (prescriptionId, { rejectWithValue }) => {
    try {
      const response = await RxntService.refillPrescription(prescriptionId);
      if (response.code === 201 || response.code === OK) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const reinstatePrescription = createAsyncThunk(
  'rxnt/reinstatePrescription',
  async (prescriptionId, { rejectWithValue }) => {
    try {
      const response = await RxntService.reinstatePrescription(prescriptionId);
      if (response.code === OK) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  settings: {
    rx_enabled: false,
    has_credentials: false,
    loading: false,
    error: null,
  },
  prescriptions: {
    data: [],
    loading: false,
    error: null,
  },
  providers: {
    data: [],
    loading: false,
    error: null,
  },
  pharmacySearch: {
    results: [],
    loading: false,
    loadingMore: false,
    error: null,
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  },
  medicationSearch: {
    results: [],
    loading: false,
    loadingMore: false,
    error: null,
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  },
  prescriptionForm: {
    loading: false,
    error: null,
    success: false,
  },
  selectedClinic: null,
  selectedProvider: null,
  selectedPharmacy: null,
  selectedMedications: [],
};

export const RxntSlice = createSlice({
  name: 'rxnt',
  initialState,
  reducers: {
    resetRxntState: () => initialState,
    setSelectedClinic: (state, action) => {
      state.selectedClinic = action.payload;
      state.selectedProvider = null;
    },
    setSelectedProvider: (state, action) => {
      state.selectedProvider = action.payload;
    },
    setSelectedPharmacy: (state, action) => {
      state.selectedPharmacy = action.payload;
    },
    addSelectedMedication: (state, action) => {
      state.selectedMedications.push(action.payload);
    },
    removeSelectedMedication: (state, action) => {
      state.selectedMedications = state.selectedMedications.filter(
        (med, index) => index !== action.payload
      );
    },
    updateSelectedMedication: (state, action) => {
      const { index, data } = action.payload;
      if (state.selectedMedications[index]) {
        state.selectedMedications[index] = {
          ...state.selectedMedications[index],
          ...data,
        };
      }
    },
    clearSelectedMedications: (state) => {
      state.selectedMedications = [];
    },
    clearPharmacySearch: (state) => {
      state.pharmacySearch.results = [];
      state.pharmacySearch.loadingMore = false;
      state.pharmacySearch.page = 1;
      state.pharmacySearch.pageSize = 50;
      state.pharmacySearch.totalCount = 0;
      state.pharmacySearch.totalPages = 0;
      state.pharmacySearch.hasNext = false;
      state.pharmacySearch.hasPrevious = false;
    },
    clearMedicationSearch: (state) => {
      state.medicationSearch.results = [];
      state.medicationSearch.loadingMore = false;
      state.medicationSearch.page = 1;
      state.medicationSearch.pageSize = 50;
      state.medicationSearch.totalCount = 0;
      state.medicationSearch.totalPages = 0;
      state.medicationSearch.hasNext = false;
      state.medicationSearch.hasPrevious = false;
    },
    resetPrescriptionForm: (state) => {
      state.prescriptionForm = {
        loading: false,
        error: null,
        success: false,
      };
      state.selectedPharmacy = null;
      state.selectedMedications = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Settings
      .addCase(fetchRxntSettings.pending, (state) => {
        state.settings.loading = true;
        state.settings.error = null;
      })
      .addCase(fetchRxntSettings.fulfilled, (state, action) => {
        state.settings.loading = false;
        state.settings.rx_enabled = action.payload?.rx_enabled ?? false;
        state.settings.has_credentials = action.payload?.has_credentials ?? false;
      })
      .addCase(fetchRxntSettings.rejected, (state, action) => {
        state.settings.loading = false;
        state.settings.error = action.payload;
      })
      // Fetch Prescriptions
      .addCase(fetchRxntPrescriptions.pending, (state) => {
        state.prescriptions.loading = true;
        state.prescriptions.error = null;
      })
      .addCase(fetchRxntPrescriptions.fulfilled, (state, action) => {
        state.prescriptions.loading = false;
        state.prescriptions.data = action.payload || [];
      })
      .addCase(fetchRxntPrescriptions.rejected, (state, action) => {
        state.prescriptions.loading = false;
        state.prescriptions.error = action.payload;
      })
      // Fetch Providers by Clinic
      .addCase(fetchRxntProvidersByClinic.pending, (state) => {
        state.providers.loading = true;
        state.providers.error = null;
      })
      .addCase(fetchRxntProvidersByClinic.fulfilled, (state, action) => {
        state.providers.loading = false;
        state.providers.data = action.payload || [];
      })
      .addCase(fetchRxntProvidersByClinic.rejected, (state, action) => {
        state.providers.loading = false;
        state.providers.error = action.payload;
      })
      // Search Pharmacies
      .addCase(searchPharmacies.pending, (state, action) => {
        const append = action.meta?.arg?.append === true;
        state.pharmacySearch.error = null;
        if (append) {
          state.pharmacySearch.loadingMore = true;
        } else {
          state.pharmacySearch.loading = true;
        }
      })
      .addCase(searchPharmacies.fulfilled, (state, action) => {
        state.pharmacySearch.loading = false;
        state.pharmacySearch.loadingMore = false;
        const append = action.meta?.arg?.append === true;
        const data = action.payload || {};
        const items = Array.isArray(data.items) ? data.items : [];
        if (append) {
          const seen = new Set(
            state.pharmacySearch.results.map(
              (p) => p.pharmacyId ?? p.id
            )
          );
          const merged = [...state.pharmacySearch.results];
          for (const p of items) {
            const id = p.pharmacyId ?? p.id;
            if (id != null && !seen.has(id)) {
              seen.add(id);
              merged.push(p);
            }
          }
          state.pharmacySearch.results = merged;
        } else {
          state.pharmacySearch.results = items;
        }
        state.pharmacySearch.totalCount = data.totalCount ?? 0;
        state.pharmacySearch.page = data.currentPage ?? 1;
        state.pharmacySearch.pageSize = data.pageSize ?? 50;
        state.pharmacySearch.totalPages = data.totalPages ?? 0;
        state.pharmacySearch.hasNext = data.hasNext ?? false;
        state.pharmacySearch.hasPrevious = data.hasPrevious ?? false;
      })
      .addCase(searchPharmacies.rejected, (state, action) => {
        state.pharmacySearch.loading = false;
        state.pharmacySearch.loadingMore = false;
        state.pharmacySearch.error = action.payload;
      })
      // Search Medications
      .addCase(searchMedications.pending, (state, action) => {
        const append = action.meta?.arg?.append === true;
        state.medicationSearch.error = null;
        if (append) {
          state.medicationSearch.loadingMore = true;
        } else {
          state.medicationSearch.loading = true;
        }
      })
      .addCase(searchMedications.fulfilled, (state, action) => {
        state.medicationSearch.loading = false;
        state.medicationSearch.loadingMore = false;
        const append = action.meta?.arg?.append === true;
        const data = action.payload || {};
        const items = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);

        if (append) {
          const seen = new Set(
            state.medicationSearch.results.map((m) => m.drugId ?? m.id)
          );
          const merged = [...state.medicationSearch.results];
          for (const item of items) {
            const id = item.drugId ?? item.id;
            if (id != null && !seen.has(id)) {
              seen.add(id);
              merged.push(item);
            }
          }
          state.medicationSearch.results = merged;
        } else {
          state.medicationSearch.results = items;
        }
        state.medicationSearch.totalCount = data.totalCount ?? 0;
        state.medicationSearch.page = data.currentPage ?? 1;
        state.medicationSearch.pageSize = data.pageSize ?? 50;
        state.medicationSearch.totalPages = data.totalPages ?? 0;
        state.medicationSearch.hasNext = data.hasNext ?? false;
        state.medicationSearch.hasPrevious = data.hasPrevious ?? false;
      })
      .addCase(searchMedications.rejected, (state, action) => {
        state.medicationSearch.loading = false;
        state.medicationSearch.loadingMore = false;
        state.medicationSearch.error = action.payload;
      })
      // Create Prescription
      .addCase(createPrescription.pending, (state) => {
        state.prescriptionForm.loading = true;
        state.prescriptionForm.error = null;
        state.prescriptionForm.success = false;
      })
      .addCase(createPrescription.fulfilled, (state, action) => {
        state.prescriptionForm.loading = false;
        state.prescriptionForm.success = true;
        state.prescriptions.data = [action.payload, ...state.prescriptions.data];
      })
      .addCase(createPrescription.rejected, (state, action) => {
        state.prescriptionForm.loading = false;
        state.prescriptionForm.error = action.payload;
      })
      // Void Prescription
      .addCase(voidPrescription.fulfilled, (state, action) => {
        const index = state.prescriptions.data.findIndex(
          (p) => p.id === action.payload.id
        );
        if (index !== -1) {
          state.prescriptions.data[index] = action.payload;
        }
      })
      // Refill Prescription
      .addCase(refillPrescription.fulfilled, (state, action) => {
        state.prescriptions.data = [action.payload, ...state.prescriptions.data];
      })
      // Reinstate Prescription
      .addCase(reinstatePrescription.fulfilled, (state, action) => {
        const index = state.prescriptions.data.findIndex(
          (p) => p.id === action.payload.id
        );
        if (index !== -1) {
          state.prescriptions.data[index] = action.payload;
        }
      });
  },
});

export const {
  resetRxntState,
  setSelectedClinic,
  setSelectedProvider,
  setSelectedPharmacy,
  addSelectedMedication,
  removeSelectedMedication,
  updateSelectedMedication,
  clearSelectedMedications,
  clearPharmacySearch,
  clearMedicationSearch,
  resetPrescriptionForm,
} = RxntSlice.actions;

export default RxntSlice.reducer;
